import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/lib/roles";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";
import { Printer, IdCard, Search, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { DFA_LOGO_SRC, programmeClassLabel } from "@/pages/identityCardUtils";

const requestKey = () => `id-card-${Date.now()}-${crypto.randomUUID()}`;
const Detail = ({ title, value, wide = false }) => (
  <div className={`id-card-detail${wide ? " id-card-detail--wide" : ""}`}>
    <div className="id-card-label">{title}</div>
    <div className="id-card-value">{value || "—"}</div>
  </div>
);
const Photo = ({ data }) => data.photo ? (
  <img src={data.photo} alt={data.full_name} className="id-card-photo" />
) : (
  <div className="id-card-photo id-card-photo--placeholder" aria-label="Photograph not available">
    <IdCard aria-hidden="true" />
    <span>PHOTO</span>
  </div>
);
const SecureQr = ({ issued }) => issued?.verification_url ? (
  <div className="id-card-qr id-card-qr--issued">
    <QRCodeSVG value={issued.verification_url} size={92} fgColor="#1B2C5A" level="M" />
    <span>SCAN TO VERIFY</span>
  </div>
) : (
  <div className="id-card-qr id-card-qr--preview" aria-label="Preview only; QR code appears after issuance">
    <div className="id-card-qr-placeholder" aria-hidden="true" />
    <strong>PREVIEW · NOT ISSUED</strong>
    <span>QR CODE WILL APPEAR AFTER ISSUANCE</span>
  </div>
);
const IdentityCard = ({ data, issued }) => {
  const student = data.person_type === "student";
  const status = issued?.status || issued?.card_status || (issued ? "active" : "preview");
  return (
  <div
    id="id-card"
    className={`id-card id-card--${student ? "student" : "staff"}`}
    data-card-orientation={student ? "landscape" : "portrait"}
  >
    <header className="id-card-header">
      <img
        src={DFA_LOGO_SRC}
        alt="DFA Logo"
        className="id-card-logo"
      />
      <div className="id-card-heading">
        <div className="id-card-school">DARUSSALAM FOUNDATION ACADEMY</div>
        <div className="id-card-kind">OFFICIAL {data.person_type.toUpperCase()} ID</div>
      </div>
    </header>
    <main className="id-card-body">
      <Photo data={data} />
      <section className="id-card-identity">
        <Detail title="FULL NAME" value={data.full_name} wide />
        <div className="id-card-details">
          <Detail title={student ? "ADMISSION NUMBER" : "STAFF ID"} value={data.identity_number} />
          {student ? (
            <>
              <Detail title="ACADEMIC SESSION" value={data.session} />
              {data.gender && <Detail title="GENDER" value={data.gender} />}
              <div className="id-card-programmes">
                <div className="id-card-label">PROGRAMMES / CLASSES</div>
                <ul>
                  {(data.programmes || []).map((placement, index) => (
                    <li key={`${placement.program}-${placement.class_name}-${index}`}>
                      {programmeClassLabel(placement)}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <>
              <Detail title="POSITION" value={data.position} />
              <Detail title="UNIT" value={data.unit} />
            </>
          )}
        </div>
      </section>
      <SecureQr issued={issued} />
    </main>
    <footer className="id-card-footer">
      <span>If found, please return to Darussalam Foundation Academy.</span>
      <span className="id-card-meta"><strong>{status.toUpperCase()}</strong> · {issued?.card_id || "NOT ISSUED"}</span>
    </footer>
  </div>
  );
};

export default function IDCards() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get("type") || "student");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [issued, setIssued] = useState(null);
  const [checked, setChecked] = useState([]);
  const canIssue = hasCapability(user, "id_card.issue"),
    canBulk = hasCapability(user, "id_card.bulk_generate");
  useEffect(() => {
    api
      .get("/id-cards/people", {
        params: { person_type: type, q, page, page_size: 25 },
      })
      .then(({ data }) => {
        setRows(data.items);
        const wanted = params.get("id");
        if (wanted)
          setSelected(data.items.find((x) => x.id === wanted) || null);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [type, q, page, params]);
  useEffect(() => {
    setPreview(null);
    setIssued(null);
  }, [selected]);
  const show = async () => {
    try {
      const { data } = await api.post("/id-cards/preview", {
        person_type: type,
        person_id: selected.id,
      });
      setPreview(data);
      setIssued(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const issue = async () => {
    try {
      const { data } = await api.post("/id-cards/issue", {
        person_type: type,
        person_id: selected.id,
        request_key: requestKey(),
      });
      setIssued(data);
      toast.success(
        data.created ? "Official card issued" : "Existing active card loaded",
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const bulk = async () => {
    const cards = rows
      .filter((x) => checked.includes(x.id))
      .map((x) => ({ person_type: type, person_id: x.id, request_key: requestKey() }));
    try {
      const { data } = await api.post("/id-cards/bulk-issue", { cards });
      toast.success(`${data.cards.length} people processed`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const openPdf = async () => {
    const { data } = await api.get(`/id-cards/${issued.card_id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data); window.open(url, "_blank", "noopener"); setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const revoke = async () => {
    const reason = window.prompt("Revocation reason"); if (!reason) return;
    try { const { data } = await api.post(`/id-cards/${issued.card_id}/revoke`, { reason, request_key: requestKey() }); setIssued({ ...issued, ...data }); toast.success("Card revoked"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const replace = async () => {
    const reason = window.prompt("Replacement reason"); if (!reason) return;
    try { const { data } = await api.post(`/id-cards/${issued.card_id}/replace`, { reason, request_key: requestKey() }); setIssued(data); toast.success("Replacement card issued"); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="idcards-page">
      <PageHeader
        overline="Identity"
        title="ID Card Generator"
        description="Canonical student placements and substantive staff appointments."
      />
      <div className="flex flex-wrap gap-2">
        <Button
          variant={type === "student" ? "default" : "outline"}
          onClick={() => {
            setType("student");
            setSelected(null);
          }}
        >
          Students
        </Button>
        <Button
          variant={type === "staff" ? "default" : "outline"}
          onClick={() => {
            setType("staff");
            setSelected(null);
          }}
        >
          Staff
        </Button>
        <div className="relative min-w-64 flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4" />
          <Input
            className="pl-9"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder={`Search ${type} name or ID`}
          />
        </div>
        {canBulk && (
          <Button variant="outline" disabled={!checked.length} onClick={bulk}>
            Issue selected
          </Button>
        )}
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="max-h-[68vh] overflow-y-auto p-3">
          {rows.map((x) => (
            <div
              key={x.id}
              className={`flex items-center rounded p-2 ${selected?.id === x.id ? "bg-[#1B2C5A] text-white" : "hover:bg-slate-50"}`}
            >
              <input type="checkbox" className="mr-2" checked={checked.includes(x.id)} onChange={(e) => setChecked((p) => e.target.checked ? [...p, x.id] : p.filter((v) => v !== x.id))} />
              <button
                className="flex-1 text-left"
                onClick={() => setSelected(x)}
              >
                <div className="font-medium">{x.full_name}</div>
                <div className="text-xs opacity-75">
                  {x.identity_number}
                  {x.position ? ` · ${x.position} · ${x.unit}` : ""}
                </div>
                {type === "student" && <div className="mt-1 text-[10px] opacity-75">{(x.enrollments || []).map((e) => `${e.program} · ${e.class_name}${e.arm ? ` ${e.arm}` : ""}`).join(" | ")}</div>}
              </button>
            </div>
          ))}
          <div className="mt-3 flex justify-between">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </Card>
        <div className="space-y-4 lg:col-span-2">
          {!selected ? (
            <div className="py-20 text-center text-slate-500">
              <IdCard className="mx-auto mb-3 h-12 w-12" />
              Select a person.
            </div>
          ) : (
            <>
              <Card className="space-y-3 p-4">
                <strong>{selected.full_name}</strong>
                {type === "student" && <p className="text-sm text-slate-600">One official card will include all active programme and class placements.</p>}
                {selected.acting_appointment_active && (
                  <p className="text-sm text-amber-700">
                    Acting appointment active; the official card retains the
                    substantive position.
                  </p>
                )}
                <Button onClick={show}>Preview</Button>
              </Card>
              {preview && (
                <>
                  <IdentityCard data={preview} issued={issued} />
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" onClick={() => window.print()}>
                      <Printer className="mr-2 h-4 w-4" />
                      Print preview
                    </Button>
                    {canIssue && !issued && (
                      <Button onClick={issue}>
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        Official issue
                      </Button>
                    )}
                    {issued && (
                      <>
                        <Button
                          variant="outline"
                          onClick={async () => {
                            await api.post(
                              `/id-cards/${issued.card_id}/reprint`,
                            );
                            window.print();
                          }}
                        >
                          Reprint
                        </Button>
                        <Button variant="outline" onClick={openPdf}>
                          <Download className="mr-2 h-4 w-4" />
                          PDF
                        </Button>
                        {hasCapability(user, "id_card.reissue") && <Button variant="outline" onClick={replace}>Replace</Button>}
                        {hasCapability(user, "id_card.revoke") && <Button variant="destructive" onClick={revoke}>Revoke</Button>}
                      </>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
